import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Renderers } from "../render/prose";

/** The `term.code/v1` shape — a block of code or terminal output, rendered
 * by the code block this repo already owns.
 *
 * It used to be a hand-styled `<pre>`. That was the same mistake as prose's
 * hand-rolled markdown: a code block exists, it carries the palette's
 * syntax tokens (`--fg-syntax-*`), and it already solves the parts a `<pre>`
 * gets wrong — long lines, selection, and a theme that tracks light and
 * dark.
 *
 * ## Read-only is structural now, not a flag
 *
 * The first fix reached one layer too low, for `std-codemirror`, and asked
 * it for `editable: false`. That is an EDITOR being told not to edit, and
 * the review kept coming back to it: a reader could not tell by looking,
 * and neither could the next person to touch this file. Nothing stops a
 * later revision from passing `true`, or from forgetting the argument and
 * getting an editable terminal transcript.
 *
 * `std-code` is the layer above, and it is the answer because of what it
 * does NOT have: there is no editable branch in it at all. It calls
 * `LonaCodeMirror.createReadOnly` and that is the only constructor in the
 * file. Read-only stops being a value this component remembers to pass and
 * becomes a fact about the component it mounts — which is the difference
 * between a convention and a guarantee.
 *
 * It also widens `language` for free. `std-code` resolves lisp, js, ts,
 * rust and plain, so the type below is its set rather than the two the
 * lower layer left us with. `plain` stays the default, because a terminal
 * block is text until a producer says otherwise.
 *
 * ## The highlighter may not arrive, and silence was the bug
 *
 * `std-code` loads its plugin at runtime from the static host, and in a
 * sandboxed review card that fetch fails outright — measured, and still
 * true today: `/@/static/js/codemirror.min.js` answers 404 on the review
 * node. An earlier revision fired `bind()` with `void`, so the rejection
 * went nowhere and the block rendered as an empty framed box. The one
 * surface where this primitive is reviewable at all showed nothing, and
 * there was no error to explain why.
 *
 * So the failure is caught and the text renders in a plain `<pre>`. That
 * is a real fallback rather than an apology: this block's content is
 * exactly the thing a `<pre>` is for, the fallback cannot take focus or a
 * caret under any circumstances, and the only thing lost is the syntax
 * colour. Read-only holds on both paths — by construction on one and by
 * the element's own nature on the other.
 *
 * Which means the review card shows the `<pre>`, not the highlighter. It
 * is the honest thing to say up front: what a card can confirm here is the
 * frame, the metrics and the fallback. The highlighted path is checkable
 * in the app, where the static host serves the plugin.
 *
 * Honest status: nothing emits `term.code/v1` yet. Across 28,127 live
 * records not one carries a record-level datatype, so this primitive has
 * never rendered outside its own storybook. It is built against the shape
 * the tsh shapes vocabulary defines, and the storybook is the only place
 * its behaviour is currently checkable. */
@component({ name: "std-turn-term-block" })
export class TurnTermBlock extends LonaWebComponent {
  /** The code renderer paints into this — std-code with its plain-text
   * understudy when the tento-ui renderers are installed (render/code.ts,
   * adapters/tento-ui.ts), a `<pre>` otherwise. Read-only on every path. */
  private $code: HTMLElement = this.$("code");

  static makeWith(props: TurnTermBlock.Props): TurnTermBlock {
    return (TurnTermBlock.make() as TurnTermBlock).bind(props);
  }

  bind(props: TurnTermBlock.Props): this {
    // Not awaited — the transcript measures rows with a ResizeObserver, so a
    // block that fills a later frame re-reports its own height. The renderer
    // owns the highlighter's fallback (the plugin fetch is the failure this
    // block is most likely to meet; the seam's tento-ui renderer catches it
    // and shows a pre).
    void Renderers.code().render(this.$code, { text: props.text, language: props.language ?? "plain" });
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
      #frame {
        border: 1px solid var(--divider, rgba(127, 127, 127, 0.25));
        border-radius: 6px;
        background: var(--surface-sunken, rgba(127, 127, 127, 0.06));
        overflow: hidden;
      }
      /* std-code brings its own box — a surface, an 8px radius and a 12px
         block margin — because it is normally a standalone block in a
         document. Here #frame is already that box, so the inner one is
         undressed rather than nested inside it. An outer-tree rule is the
         one thing that outranks the component's own :host, which is
         exactly what undressing it needs.

         A transcript row is also not a scroll container, so the block must
         not claim a viewport height of its own. */
      #code {
        display: block;
        height: auto;
        margin-block: 0;
        border-radius: 0;
        background: transparent;
        --std-code-padding: 6px 8px;
        --font-size: var(--turn-fine-size, var(--text-8px));
      }
      /* Only ever visible when the highlighter could not load — which is
         every review card, since the static host 404s the plugin there.
         Same metrics and the same inset as the block it replaces, so the
         frame is the same size whichever path runs. */
      #fallback {
        display: none;
        margin: 0;
        padding: 6px 8px;
        --font-size: var(--turn-fine-size, var(--text-8px));
        --line-height-ratio: 1.5;
        color: var(--text-sec);
        white-space: pre;
        overflow-x: auto;
      }
      :host([fallback]) #code {
        display: none;
      }
      :host([fallback]) #fallback {
        display: block;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="frame">
      <div id="code"></div>
    </div>
  `;
}

export namespace TurnTermBlock {
  /** std-code's own set. Anything it does not recognise it resolves to
   * `plain`, so this is a promise about what will be COLOURED, not a
   * validator — widening it past this list means adding a language pack to
   * the codemirror bundle, which is that bundle's decision. */
  export type Language = "plain" | "lisp" | "js" | "ts" | "rust";

  export type Props = {
    text: string;
    language?: Language;
  };
}
